import { useEffect, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import type { DashboardPageId } from '@proadv/types';
import { cn } from '@/lib/utils';

import {
  WIDGET_IDS_BY_PAGE,
  getWidgetRegistry,
} from './widget-registry';

interface AddWidgetMenuProps {
  pageId: DashboardPageId;
  presentIds: string[];
  onAdd: (widgetId: string) => void;
}

/**
 * Dropdown simples para escolher um widget que ainda nao esta no layout.
 * Fecha ao clicar fora ou ao apertar Esc. O catalogo disponivel depende
 * da pagina (home tem 8 widgets, clients tem 7).
 */
export function AddWidgetMenu({
  pageId,
  presentIds,
  onAdd,
}: AddWidgetMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const registry = getWidgetRegistry(pageId);
  const allIds = WIDGET_IDS_BY_PAGE[pageId];
  const available = allIds.filter((id) => !presentIds.includes(id));

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const disabled = available.length === 0;

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition',
          'border-[var(--border)] bg-[var(--surface)] text-[var(--fg)]',
          disabled
            ? 'cursor-not-allowed opacity-50'
            : 'hover:bg-[var(--surface-muted)]',
        )}
      >
        <Plus className="h-3.5 w-3.5" />
        Adicionar widget
      </button>
      {open && !disabled && (
        <div
          className={cn(
            'absolute right-0 z-20 mt-1 w-64 overflow-hidden rounded-md border shadow-lg',
            'popover-menu',
          )}
        >
          <ul className="max-h-72 overflow-y-auto py-1">
            {available.map((id) => {
              const meta = registry[id];
              if (!meta) return null;
              const Icon = meta.icon;
              return (
                <li key={id}>
                  <button
                    type="button"
                    onClick={() => {
                      onAdd(id);
                      setOpen(false);
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-xs',
                      'text-[var(--fg)] hover:bg-[var(--surface-muted)]',
                    )}
                  >
                    <Icon className="h-3.5 w-3.5 text-[var(--fg-muted)]" />
                    {meta.title}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
